import { Locale } from '@doss/prisma/types'
import { Delimiter } from './csv.js'

export enum LocaleCode {
  EN_US = 'en-US',
  EN_CA = 'en-CA',
  FR_CA = 'fr-CA',
  DE_DE = 'de-DE',
  FR_FR = 'fr-FR',
  EN_IE = 'en-IE',
  NL_NL = 'nl-NL',
  EN_GB = 'en-GB',
  EN_NZ = 'en-NZ',
  ZH_HK = 'zh-HK',
  ZH_SG = 'zh-SG',
  HE_IL = 'he-IL',
  ES_ES = 'es-ES',
  ES_MX = 'es-MX',
}

export function isLocaleCode(locale: string): locale is LocaleCode {
  return Object.values(LocaleCode).includes(locale as LocaleCode)
}

export const LocaleCodeMap: Record<Locale, LocaleCode> = {
  [Locale.EN_US]: LocaleCode.EN_US,
  [Locale.EN_CA]: LocaleCode.EN_CA,
  [Locale.FR_CA]: LocaleCode.FR_CA,
  [Locale.DE_DE]: LocaleCode.DE_DE,
  [Locale.FR_FR]: LocaleCode.FR_FR,
  [Locale.EN_IE]: LocaleCode.EN_IE,
  [Locale.NL_NL]: LocaleCode.NL_NL,
  [Locale.EN_GB]: LocaleCode.EN_GB,
  [Locale.EN_NZ]: LocaleCode.EN_NZ,
  [Locale.ZH_HK]: LocaleCode.ZH_HK,
  [Locale.ZH_SG]: LocaleCode.ZH_SG,
  [Locale.HE_IL]: LocaleCode.HE_IL,
  [Locale.ES_ES]: LocaleCode.ES_ES,
  [Locale.ES_MX]: LocaleCode.ES_MX,
}

export const LocaleDisplayNameMap: Record<Locale, string> = {
  [Locale.EN_US]: 'English (US)',
  [Locale.EN_CA]: 'English (Canada)',
  [Locale.FR_CA]: 'French (Canada)',
  [Locale.DE_DE]: 'German (Germany)',
  [Locale.FR_FR]: 'French (France)',
  [Locale.EN_IE]: 'English (Ireland)',
  [Locale.NL_NL]: 'Dutch (Netherlands)',
  [Locale.EN_GB]: 'English (United Kingdom)',
  [Locale.EN_NZ]: 'English (New Zealand)',
  [Locale.ZH_HK]: 'Chinese (Hong Kong)',
  [Locale.ZH_SG]: 'Chinese (Singapore)',
  [Locale.HE_IL]: 'Hebrew (Israel)',
  [Locale.ES_ES]: 'Spanish (Spain)',
  [Locale.ES_MX]: 'Spanish (Mexico)',
}

/**
 * Get the shortform locale code for a given locale.
 * i.e. 'en-US' -> 'en'
 *
 * Default to 'en' if the locale is not found in the known locale codes
 */
export function getShortformLocale(locale: Locale): string {
  return LocaleCodeMap[locale]?.split('-')?.[0] ?? 'en'
}

/**
 * Get the long form date format for a given locale.
 * These locales are used in generating regex patterns for parsing dates,
 * but the regex patterns will accommodate all variations of digits for m, d, y.
 */
export const LocaleCodeToDateFormatMap: Record<LocaleCode, string> = {
  [LocaleCode.EN_US]: 'MM/DD/YYYY',
  [LocaleCode.EN_CA]: 'MM/DD/YYYY',
  [LocaleCode.FR_CA]: 'DD/MM/YYYY',
  [LocaleCode.DE_DE]: 'DD.MM.YYYY',
  [LocaleCode.FR_FR]: 'DD/MM/YYYY',
  [LocaleCode.EN_IE]: 'DD/MM/YYYY',
  [LocaleCode.NL_NL]: 'DD-MM-YYYY',
  [LocaleCode.EN_GB]: 'DD/MM/YYYY',
  [LocaleCode.EN_NZ]: 'DD/MM/YYYY',
  [LocaleCode.ZH_HK]: 'DD/MM/YYYY',
  [LocaleCode.ZH_SG]: 'DD/MM/YYYY',
  [LocaleCode.HE_IL]: 'DD/MM/YYYY',
  [LocaleCode.ES_ES]: 'DD/MM/YYYY',
  [LocaleCode.ES_MX]: 'DD/MM/YYYY',
}

export const LocaleCodeToTimeFormatMap: Record<LocaleCode, string> = {
  [LocaleCode.EN_US]: 'h:mm A', // 12-hour format with AM/PM
  [LocaleCode.EN_CA]: 'h:mm A', // 12-hour format with AM/PM
  [LocaleCode.FR_CA]: 'HH:mm', // 24-hour format
  [LocaleCode.DE_DE]: 'HH:mm', // 24-hour format
  [LocaleCode.FR_FR]: 'HH:mm', // 24-hour format
  [LocaleCode.EN_IE]: 'HH:mm', // 24-hour format
  [LocaleCode.NL_NL]: 'HH:mm', // 24-hour format
  [LocaleCode.EN_GB]: 'HH:mm', // 24-hour format
  [LocaleCode.EN_NZ]: 'h:mm A', // 12-hour format with AM/PM
  [LocaleCode.ZH_HK]: 'HH:mm', // 24-hour format
  [LocaleCode.ZH_SG]: 'HH:mm', // 24-hour format
  [LocaleCode.HE_IL]: 'HH:mm', // 24-hour format
  [LocaleCode.ES_ES]: 'HH:mm', // 24-hour format
  [LocaleCode.ES_MX]: 'HH:mm', // 24-hour format
}

export const LocaleCodeToDelimiterMap: Record<LocaleCode, Delimiter> = {
  [LocaleCode.EN_US]: ',', // Comma is common in US
  [LocaleCode.EN_CA]: ',', // Comma is also used in English-speaking Canada
  [LocaleCode.FR_CA]: ';', // Semicolon is common in French-speaking Canada
  [LocaleCode.DE_DE]: ';', // Semicolon is often used in Germany
  [LocaleCode.FR_FR]: ';', // Semicolon is commonly used in France
  [LocaleCode.EN_IE]: ',', // Comma is common in Ireland
  [LocaleCode.NL_NL]: ';', // Semicolon is used in the Netherlands
  [LocaleCode.EN_GB]: ',', // Comma is standard in the UK
  [LocaleCode.EN_NZ]: ',', // Comma is common in New Zealand
  [LocaleCode.ZH_HK]: ',', // Comma is common in Hong Kong
  [LocaleCode.ZH_SG]: ',', // Comma is used in Singapore
  [LocaleCode.HE_IL]: ',', // Comma is used in Israel
  [LocaleCode.ES_ES]: ';', // Semicolon is commonly used in Spain
  [LocaleCode.ES_MX]: ',', // Comma is common in Mexico
}

export const LocaleCodeToNumberFormatExampleMap: Record<LocaleCode, string> = {
  [LocaleCode.EN_US]: '1,234.56',
  [LocaleCode.EN_CA]: '1,234.56',
  [LocaleCode.FR_CA]: '1 234,56',
  [LocaleCode.DE_DE]: '1.234,56',
  [LocaleCode.FR_FR]: '1 234,56',
  [LocaleCode.EN_IE]: '1,234.56',
  [LocaleCode.NL_NL]: '1.234,56',
  [LocaleCode.EN_GB]: '1,234.56',
  [LocaleCode.EN_NZ]: '1,234.56',
  [LocaleCode.ZH_HK]: '1,234.56',
  [LocaleCode.ZH_SG]: '1,234.56',
  [LocaleCode.HE_IL]: '1,234.56',
  [LocaleCode.ES_ES]: '1.234,56',
  [LocaleCode.ES_MX]: '1,234.56',
}

export const LocaleCodeToPatternMap: Record<LocaleCode, string> = {
  [LocaleCode.EN_US]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.EN_CA]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.FR_CA]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.DE_DE]: '\\d{2}\\.\\d{2}\\.\\d{4}',
  [LocaleCode.FR_FR]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.EN_IE]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.NL_NL]: '\\d{2}-\\d{2}-\\d{4}',
  [LocaleCode.EN_GB]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.EN_NZ]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.ZH_HK]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.ZH_SG]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.HE_IL]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.ES_ES]: '\\d{2}/\\d{2}/\\d{4}',
  [LocaleCode.ES_MX]: '\\d{2}/\\d{2}/\\d{4}',
}
